/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isString from 'lodash/isString';

/**
 * Knihovna utilit pro práci s čísly.
 */
export default class Numeric {
  /**
   * Určí, jestli je předaná hodnota platným indexem pole.
   * Index je celé číslo v intervalu [0, {@link Number.MAX_SAFE_INTEGER}].
   * @param {number} value Hodnota k ověření.
   * @returns {value is number} Příznak, jestli jsou požadavky splněny.
   */
  static isIndex(value) {
    return Number.isSafeInteger(value) && value >= 0;
  }

  /**
   * Zkonvertuje string na index, tj. nezáporné celé číslo.
   * @param {string} value
   * @param {number} [radix = 10] Radix neboli báze číselné soustavy, defaultně dekadická.
   * @returns {number}
   * @throws {TypeError} Pokud hodnota není string.
   * @throws {RangeError} Pokud naparsované číslo není {@link isIndex index}.
   */
  static parseIndex(value, radix = 10) {
    // TODO: Možná zbytečné runtime kontrola pro robustnost.
    if (!isString(value)) {
      throw new TypeError('value is not a string');
    }

    // Pozor, parseInt (i parseFloat) naparsuje a přijme neočekávaně i nějaký
    // další text za prvním číslem! Proto je potřeba naparsovanou hodnotu převést
    // zpět na string a zkontrolovat shodu.
    const n = parseInt(value, radix);
    if (n.toString(radix) !== value || !this.isIndex(n)) {
      throw new RangeError(
        'parsed number is not a index (non-negative safe integer)'
      );
    }

    return n;
  }

  /**
   * Vrátí stringovou reprezentaci čísla {@link value} případně doplněnou nulami
   * zleva na {@link digits} číslic.
   * @param {number} value Celočíselná nezáporná hodnota.
   * @param {number} digits Celočíselný kladný počet číslic na výstupu.
   * @param {number} [radix = 10] Radix neboli báze číselné soustavy, defaultně dekadická.
   * @returns {string} Stringová reprezentace čísla případně doplněná nulami zleva.
   * @throws {TypeError} Pokud {@link value} není celočíselná nezáporná hodnota.
   * @throws {RangeError} Pokud je počet číslic {@link value} větší než {@link digits}.
   */
  static toStringPadded(value, digits, radix = 10) {
    if (!this.isIndex(value)) {
      throw new TypeError('value is not a non-negative integer');
    }
    if (!this.isIndex(digits) || digits === 0) {
      throw new TypeError('digits are not a positive integer');
    }
    const s = value.toString(radix);
    if (s.length > digits) {
      throw new RangeError('value is too big');
    }
    return s.padStart(digits, '0');
  }
}
